import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Typography,
} from "@mui/material";
import axiosInstance from "../api/axiosConfig";
import {
  DEV_SITE_URL_KEYWORD,
  clearDevSiteOverrides,
  countMarkedDevSites,
  markMatchingSitesAsDev,
  useDevSiteOverrides,
} from "../utils/devSites";

const SYNC_POLL_INTERVAL_MS = 3000;

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const SettingsDialog = ({ open, onClose, onSynced }) => {
  const overrides = useDevSiteOverrides();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [syncSkipUninstalled, setSyncSkipUninstalled] = useState(true);
  const [syncJob, setSyncJob] = useState(null);
  const [syncStarting, setSyncStarting] = useState(false);
  const [syncError, setSyncError] = useState(null);

  const markedCount = countMarkedDevSites(overrides);
  const syncRunning = syncJob?.status === "running";

  const handleClose = () => {
    setResult(null);
    setSyncError(null);
    setConfirmingClear(false);
    onClose();
  };

  const readSyncStatus = useCallback(async () => {
    const response = await axiosInstance.get("/admin/sync-instances/status");
    return response.data?.job ?? null;
  }, []);

  // The sweep runs on the server, so reopening the dialog (or reloading the
  // page) picks up whatever is currently in flight instead of losing it.
  useEffect(() => {
    if (!open) return undefined;
    let stale = false;
    readSyncStatus()
      .then((job) => {
        if (!stale) setSyncJob(job);
      })
      .catch(() => {});
    return () => {
      stale = true;
    };
  }, [open, readSyncStatus]);

  useEffect(() => {
    if (!open || !syncRunning) return undefined;

    const timer = setInterval(async () => {
      try {
        const job = await readSyncStatus();
        setSyncJob(job);
        if (job && job.status !== "running") {
          if (job.updated > 0 || job.uninstalled > 0) onSynced?.();
        }
      } catch (error) {
        // A dropped poll is not a failed sync — the next tick tries again.
        console.warn("Could not read sync status:", error.message);
      }
    }, SYNC_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [open, syncRunning, readSyncStatus, onSynced]);

  const handleSyncFromWix = async () => {
    setSyncStarting(true);
    setSyncError(null);
    try {
      // An empty object, not null: the instance sends Content-Type
      // application/json on every request, so axios serialises whatever it is
      // given and the server's JSON body parser rejects a bare `null` with 400.
      const response = await axiosInstance.post(
        "/admin/sync-instances",
        {},
        {
          params: syncSkipUninstalled ? { onlyInstalled: true } : undefined,
        },
      );
      setSyncJob(response.data?.job ?? null);
    } catch (error) {
      // 409 means a sweep is already in flight — attach to it rather than
      // reporting an error the admin can do nothing about.
      if (error.response?.status === 409 && error.response.data?.job) {
        setSyncJob(error.response.data.job);
      } else {
        setSyncError(
          error.response?.data?.error || error.message || "Sync failed",
        );
      }
    } finally {
      setSyncStarting(false);
    }
  };

  const handleCancelSync = async () => {
    try {
      const response = await axiosInstance.post(
        "/admin/sync-instances/cancel",
        {},
      );
      setSyncJob(response.data?.job ?? null);
    } catch (error) {
      setSyncError(error.response?.data?.error || error.message);
    }
  };

  const handleRunJob = async () => {
    setRunning(true);
    setResult(null);
    try {
      const response = await axiosInstance.get("/admin/installations");
      const installations = response.data.installations || [];
      const { matched, marked } = markMatchingSitesAsDev(installations);

      setResult({
        severity: "success",
        message:
          matched === 0
            ? `No sites with "${DEV_SITE_URL_KEYWORD}" in their URL were found.`
            : `Scanned ${installations.length} sites — ${matched} matched, ${marked} newly marked as development sites.`,
      });
    } catch (error) {
      setResult({
        severity: "error",
        message: `Job failed: ${error.message}`,
      });
    } finally {
      setRunning(false);
    }
  };

  const handleClearOverrides = () => {
    if (!confirmingClear) {
      setConfirmingClear(true);
      return;
    }
    clearDevSiteOverrides();
    setConfirmingClear(false);
    setResult({
      severity: "info",
      message: `Cleared all manual marks. Sites with "${DEV_SITE_URL_KEYWORD}" in their URL are still detected automatically.`,
    });
  };

  const renderSyncStatus = () => {
    if (syncError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Sync failed: {syncError}
        </Alert>
      );
    }
    if (!syncJob || syncJob.status === "idle") return null;

    if (syncJob.status === "running") {
      const total = syncJob.total || 0;
      const processed = syncJob.processed || 0;
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            {total > 0
              ? `Checked ${processed} of ${total} sites…`
              : "Starting…"}{" "}
            {syncJob.updated} updated, {syncJob.failed} failed.
          </Typography>
          <LinearProgress
            sx={{ mt: 1 }}
            variant={total > 0 ? "determinate" : "indeterminate"}
            value={total > 0 ? (processed / total) * 100 : 0}
          />
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "text.secondary" }}
          >
            This keeps running on the server — you can close this dialog and
            come back to it.
          </Typography>
        </Box>
      );
    }

    if (syncJob.status === "failed") {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Sync stopped after {plural(syncJob.processed, "site")}:{" "}
          {syncJob.error}
        </Alert>
      );
    }

    const severity =
      syncJob.failed > 0
        ? "warning"
        : syncJob.status === "cancelled"
          ? "info"
          : "success";

    return (
      <Alert severity={severity} sx={{ mt: 2 }}>
        {syncJob.status === "cancelled" ? "Sync cancelled after " : "Checked "}
        {plural(syncJob.processed, "site")}
        {syncJob.dryRun ? " (dry run, nothing written)" : ""} — {""}
        {syncJob.updated} updated, {syncJob.unchanged} already up to date,{" "}
        {syncJob.uninstalled} found uninstalled, {syncJob.failed} failed.
        {syncJob.failures?.length > 0 && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {syncJob.failures.slice(0, 5).map((failure) => (
              <li key={failure.id ?? failure.instanceId}>
                {failure.siteName || failure.instanceId}: {failure.error}
              </li>
            ))}
            {syncJob.failures.length > 5 && (
              <li>and {syncJob.failures.length - 5} more…</li>
            )}
          </Box>
        )}
      </Alert>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Settings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Development sites
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Development sites are excluded from installation, paid user and
          uninstall counts. Any site whose URL contains "{DEV_SITE_URL_KEYWORD}"
          is detected automatically; you can also mark or unmark a site from the
          Dev Site column on the installations table.
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Sites explicitly marked as development sites: <b>{markedCount}</b>
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Mark existing sites
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Runs a one-off job over every site added so far and marks the ones
          with "{DEV_SITE_URL_KEYWORD}" in their URL as development sites.
        </Typography>
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleRunJob}
            disabled={running}
            startIcon={
              running ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {running ? "Running…" : "Run job"}
          </Button>
          <Button
            variant="outlined"
            color={confirmingClear ? "error" : "inherit"}
            onClick={handleClearOverrides}
            disabled={running || markedCount === 0}
          >
            {confirmingClear ? "Confirm clear" : "Clear manual marks"}
          </Button>
        </Box>

        {result && (
          <Alert severity={result.severity} sx={{ mt: 2 }}>
            {result.message}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Sync installation data from Wix
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Reads the app instance for every site, one by one, and updates its
          plan, billing cycle, expiry date, install status, URL and site name
          from Wix. Use this when the stored data has drifted from Wix — for
          example a trial that converted to a paid plan without a matching
          webhook. The sweep runs on the server and takes several minutes, so it
          carries on even if you close this dialog.
        </Typography>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              size="small"
              checked={syncSkipUninstalled}
              onChange={(event) => setSyncSkipUninstalled(event.target.checked)}
              disabled={syncRunning || syncStarting}
            />
          }
          label="Skip sites already marked as uninstalled (faster)"
        />

        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSyncFromWix}
            disabled={syncRunning || syncStarting}
            startIcon={
              syncRunning || syncStarting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {syncRunning ? "Syncing…" : "Sync from Wix"}
          </Button>
          {syncRunning && (
            <Button variant="outlined" color="error" onClick={handleCancelSync}>
              Stop
            </Button>
          )}
        </Box>

        {renderSyncStatus()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
